import React, {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import type { DashboardBreadcrumb } from '@/lib/dashboardBreadcrumbs';

export interface PageHeaderState {
  title: string;
  titleAccessory: ReactNode | null;
  subtitle: ReactNode | null;
  help: ReactNode | null;
  /** Optional DOM id for the top-bar Page help trigger (guided tours). */
  helpAnchorId?: string | null;
  breadcrumbs: DashboardBreadcrumb[] | null;
}

interface PageHeaderContextValue {
  header: PageHeaderState;
  setHeader: (state: PageHeaderState) => void;
  clearHeader: () => void;
  inCardHeaderSlotEl: HTMLDivElement | null;
  setInCardHeaderSlotEl: (el: HTMLDivElement | null) => void;
}

const EMPTY_HEADER: PageHeaderState = {
  title: '',
  titleAccessory: null,
  subtitle: null,
  help: null,
  helpAnchorId: null,
  breadcrumbs: null,
};

const PageHeaderContext = createContext<PageHeaderContextValue | null>(null);

export function PageHeaderProvider({ children }: { children: ReactNode }) {
  const [header, setHeaderState] = useState<PageHeaderState>(EMPTY_HEADER);
  const [inCardHeaderSlotEl, setInCardHeaderSlotEl] = useState<HTMLDivElement | null>(null);

  const setHeader = useCallback((state: PageHeaderState) => {
    setHeaderState(state);
  }, []);

  const clearHeader = useCallback(() => {
    setHeaderState(EMPTY_HEADER);
  }, []);

  const value = useMemo(
    () => ({
      header,
      setHeader,
      clearHeader,
      inCardHeaderSlotEl,
      setInCardHeaderSlotEl,
    }),
    [header, setHeader, clearHeader, inCardHeaderSlotEl]
  );

  return <PageHeaderContext.Provider value={value}>{children}</PageHeaderContext.Provider>;
}

/** Clears registered page header state when the route changes to avoid stale breadcrumb titles. */
export function PageHeaderRouteReset() {
  const { pathname } = useLocation();
  const { clearHeader } = usePageHeaderContext();

  useLayoutEffect(() => {
    clearHeader();
  }, [pathname, clearHeader]);

  return null;
}

export function useOptionalPageHeaderContext(): PageHeaderContextValue | null {
  return useContext(PageHeaderContext);
}

export function usePageHeaderContext(): PageHeaderContextValue {
  const ctx = useOptionalPageHeaderContext();
  if (!ctx) {
    throw new Error('usePageHeaderContext must be used within PageHeaderProvider');
  }
  return ctx;
}
